/*
  A card's thumbnail in a table, with a large preview on hover AND on keyboard focus
  (Robert, 2026-10-20: "add another section like this by the card number where someone can
  hover over the card to see what it is", pointing at admin's catalog table).

  Admin's HoverPreviewOverlay follows the mouse, so a keyboard user never sees it. This one
  anchors to the thumbnail instead: the thumbnail is the card page's link, so Tab reaches
  it, and focus opens the same preview hover does. The preview is portalled to <body> and
  fixed, so the table's overflow-x scroll box cannot clip it; it sits right of the thumb
  when there is room, else left, centred on it and kept inside the viewport. A scroll
  re-anchors it to the thumbnail, so it never floats away from its row.

  The preview is decoration over the link (the link's own name is the card's), so it is
  aria-hidden. With no image the cell shows a blank card-shaped placeholder and no preview.
*/

import { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';

import { FOCUS_RING } from 'components/styles';

const PREVIEW_W = 280;
const PREVIEW_H = 391;
const GAP = 16;

interface CardThumbPreviewProps {
  image: string | null | undefined;
  to: string;
  /* The card's name: the link's accessible name. */
  name: string;
}

const CardThumbPreview = ({ image, to, name }: CardThumbPreviewProps) => {
  const anchor = useRef<HTMLAnchorElement>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);

  const open = useCallback(() => {
    if (image && anchor.current) {
      setRect(anchor.current.getBoundingClientRect());
    }
  }, [image]);
  const close = useCallback(() => setRect(null), []);

  /*
    Re-anchor rather than close on scroll: focusing the link can itself scroll the table's
    box, and closing then would hide the preview from exactly the keyboard user it is for.
  */
  const open_ = rect !== null;
  useEffect(() => {
    if (!open_) {
      return undefined;
    }
    const follow = () => {
      if (anchor.current) {
        setRect(anchor.current.getBoundingClientRect());
      }
    };
    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', follow);
    return () => {
      window.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', follow);
    };
  }, [open_]);

  let preview = null;
  if (rect && image) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const right = rect.right + GAP + PREVIEW_W <= vw;
    const left = right ? rect.right + GAP : Math.max(8, rect.left - GAP - PREVIEW_W);
    const top = Math.max(8, Math.min(rect.top + rect.height / 2 - PREVIEW_H / 2, vh - PREVIEW_H - 8));
    preview = ReactDOM.createPortal(
      <div
        aria-hidden="true"
        className="pointer-events-none fixed z-50 flex items-center justify-center"
        style={{ left, top, width: PREVIEW_W, height: PREVIEW_H }}
      >
        <img src={image} alt="" className="max-h-full max-w-full rounded-xl object-contain shadow-2xl" />
      </div>,
      document.body
    );
  }

  return (
    <>
      <Link
        ref={anchor}
        to={to}
        aria-label={name}
        onMouseEnter={open}
        onMouseLeave={close}
        onFocus={open}
        onBlur={close}
        className={`block w-[52px] rounded ${FOCUS_RING}`}
      >
        {image ? (
          <img
            src={image}
            alt=""
            loading="lazy"
            className="h-[72px] w-[52px] rounded object-contain transition-transform hover:scale-105"
          />
        ) : (
          <span className="block h-[72px] w-[52px] rounded border border-dashed border-pcg-border bg-pcg-raised" />
        )}
      </Link>
      {preview}
    </>
  );
};

export default CardThumbPreview;
